<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import {
  fetchMarketplaceJson,
  postMarketplaceJson,
  postMarketplaceJsonBody,
  patchMarketplaceJson,
} from 'dashboard/helpers/marketplaceApi';

const route = useRoute();
const accountId = route.params.accountId;

const loading = ref(false);
const errorMsg = ref('');
const pending = ref([]);
const closed = ref([]);
const stats = ref({});
const seller = ref('all');
const tab = ref('bekleyen');
const sendingId = ref(null);
const savingId = ref(null);
const refreshing = ref(false);

const SELLER_LABELS = { koza: 'Koza', modax: 'ModaXLarge' };
const CATEGORY_META = {
  beden: { label: 'Beden sorusu', cls: 'bg-violet-100 text-violet-800 dark:bg-violet-800/40 dark:text-violet-200' },
  stok_renk: { label: 'Stok / Renk', cls: 'bg-green-100 text-green-800 dark:bg-green-800/40 dark:text-green-200' },
  kumas_ozellik: { label: 'Kumaş / Özellik', cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800/40 dark:text-yellow-200' },
  kargo_teslim: { label: 'Kargo / Teslimat', cls: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-200' },
  siparis_sikayet: { label: '⚠️ Sipariş / Şikayet', cls: 'bg-red-100 text-red-800 dark:bg-red-800/40 dark:text-red-200' },
  diger: { label: 'Diğer', cls: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200' },
};
const SOURCE_LABELS = {
  panel: 'Panelden gönderildi',
  trendyol_panel: 'Trendyol panelinden cevaplandı',
  otomatik: 'Otomatik',
};

const catMeta = c => CATEGORY_META[c] || CATEGORY_META.diger;

const fmtTime = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const fmtWait = mins => {
  if (mins == null) return '';
  if (mins < 60) return `${mins} dk`;
  return `${Math.floor(mins / 60)} sa ${mins % 60} dk`;
};

const waitCls = mins => {
  if (mins == null) return '';
  if (mins <= 30) return 'text-green-700 dark:text-green-400';
  if (mins <= 120) return 'text-yellow-700 dark:text-yellow-500';
  return 'text-red-700 dark:text-red-400 font-semibold';
};

const filteredPending = computed(() =>
  pending.value.filter(q => seller.value === 'all' || q.seller === seller.value)
);
const filteredClosed = computed(() =>
  closed.value.filter(q => seller.value === 'all' || q.seller === seller.value)
);

const load = async () => {
  loading.value = true;
  errorMsg.value = '';
  try {
    const res = await fetchMarketplaceJson(accountId, 'trendyol_questions');
    pending.value = res.pending || [];
    closed.value = res.closed || [];
    stats.value = res.stats || {};
  } catch (e) {
    errorMsg.value = 'Sorular yüklenemedi. Sayfayı yenileyip tekrar deneyin.';
  } finally {
    loading.value = false;
  }
};

const togglingAuto = ref(false);
const toggleAuto = async () => {
  const next = !stats.value.auto_send;
  if (next) {
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      'Otomatik cevap AÇILSIN mı?\n\nAçıkken beden, stok ve kumaş soruları yapay zekâ tarafından ONAYSIZ cevaplanır (günde en fazla 40). Sipariş/şikayet soruları her zaman size kalır.\n\nGönderilen her otomatik cevap "Cevaplananlar" sekmesinde görünür.'
    );
    if (!ok) return;
  }
  togglingAuto.value = true;
  try {
    const res = await postMarketplaceJsonBody(accountId, 'trendyol_questions/toggle_auto', {
      enabled: next ? '1' : '0',
    });
    stats.value = { ...stats.value, auto_send: res.auto_send };
  } catch (e) {
    errorMsg.value = 'Otomatik cevap ayarı değiştirilemedi.';
  } finally {
    togglingAuto.value = false;
  }
};

const refresh = async () => {
  refreshing.value = true;
  try {
    await postMarketplaceJson(accountId, 'trendyol_questions/refresh');
    setTimeout(async () => {
      await load();
      refreshing.value = false;
    }, 6000);
  } catch (e) {
    refreshing.value = false;
  }
};

const saveDraft = async q => {
  savingId.value = q.id;
  try {
    await patchMarketplaceJson(accountId, `trendyol_questions/${q.id}`, {
      draft_text: q.draft_text,
    });
  } catch (e) {
    errorMsg.value = 'Taslak kaydedilemedi.';
  } finally {
    savingId.value = null;
  }
};

const sendAnswer = async q => {
  const text = (q.draft_text || '').trim();
  if (text.length < 10) {
    errorMsg.value = 'Cevap en az 10 karakter olmalı.';
    return;
  }
  // eslint-disable-next-line no-alert
  if (!window.confirm(`Bu cevap Trendyol'a gönderilecek:\n\n"${text}"\n\nOnaylıyor musunuz?`)) return;
  sendingId.value = q.id;
  errorMsg.value = '';
  try {
    await postMarketplaceJsonBody(accountId, `trendyol_questions/${q.id}/send_answer`, { text });
    await load();
  } catch (e) {
    errorMsg.value = 'Cevap gönderilemedi. Tekrar deneyin.';
  } finally {
    sendingId.value = null;
  }
};

onMounted(load);
</script>

<template>
  <div class="flex flex-col h-full overflow-auto bg-n-background">
    <div class="max-w-5xl w-full mx-auto px-6 py-6">
      <!-- Başlık -->
      <div class="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 class="text-2xl font-bold text-n-slate-12">Trendyol Soruları</h1>
          <p class="text-sm text-n-slate-11 mt-1">
            Müşteri soruları anlık düşer, yapay zekâ cevap taslağını hazırlar, siz onaylayıp gönderirsiniz.
          </p>
        </div>
        <div class="flex items-center gap-3">
          <button
            class="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-semibold disabled:opacity-60"
            :class="stats.auto_send
              ? 'bg-green-600 hover:bg-green-700 border-green-700 text-white'
              : 'bg-n-solid-1 hover:bg-n-solid-2 border-n-weak text-n-slate-11'"
            :disabled="togglingAuto"
            @click="toggleAuto"
          >
            <span class="w-2.5 h-2.5 rounded-full" :class="stats.auto_send ? 'bg-white' : 'bg-slate-400'" />
            {{ stats.auto_send ? 'Otomatik cevap: AÇIK' : 'Otomatik cevap: KAPALI' }}
          </button>
          <button
            class="px-4 py-2 rounded-lg bg-woot-600 hover:bg-woot-700 text-white text-sm font-medium disabled:opacity-60"
            :disabled="refreshing"
            @click="refresh"
          >
            {{ refreshing ? 'Trendyol\'dan çekiliyor…' : '🔄 Şimdi Yenile' }}
          </button>
        </div>
      </div>

      <div
        v-if="stats.auto_send"
        class="mb-4 rounded-lg bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 px-4 py-3 text-sm text-green-800 dark:text-green-200"
      >
        🤖 Otomatik cevap açık: beden, stok ve kumaş soruları yapay zekâ tarafından cevaplanıyor (bugüne kadar {{ stats.otomatik_gonderilen || 0 }} otomatik cevap).
        Sipariş/şikayet soruları her zaman burada sizi bekler.
      </div>

      <!-- İstatistik kartları -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <div class="text-3xl font-extrabold" :class="stats.bekleyen ? 'text-red-600 dark:text-red-400' : 'text-green-700 dark:text-green-400'">
            {{ stats.bekleyen ?? '–' }}
          </div>
          <div class="text-sm text-n-slate-11">Cevap bekleyen</div>
        </div>
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <div class="text-3xl font-extrabold text-n-slate-12">{{ stats.bugun_gelen ?? '–' }}</div>
          <div class="text-sm text-n-slate-11">Bugün gelen soru</div>
        </div>
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <div class="text-3xl font-extrabold text-n-slate-12">
            {{ stats.ort_yanit_saat != null ? `${stats.ort_yanit_saat} sa` : '–' }}
          </div>
          <div class="text-sm text-n-slate-11">Ort. yanıt süresi (7 gün)</div>
        </div>
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <div class="text-3xl font-extrabold text-n-slate-12">{{ stats.hafta_cevaplanan ?? '–' }}</div>
          <div class="text-sm text-n-slate-11">Son 7 günde cevaplanan</div>
        </div>
      </div>

      <!-- Filtreler -->
      <div class="flex flex-wrap items-center gap-2 mb-4">
        <div class="flex rounded-lg overflow-hidden border border-n-weak">
          <button
            v-for="t in [
              { key: 'bekleyen', label: `Bekleyenler (${filteredPending.length})` },
              { key: 'gecmis', label: 'Cevaplananlar' },
            ]"
            :key="t.key"
            class="px-4 py-2 text-sm font-medium"
            :class="tab === t.key ? 'bg-woot-600 text-white' : 'bg-n-solid-1 text-n-slate-11 hover:bg-n-solid-2'"
            @click="tab = t.key"
          >
            {{ t.label }}
          </button>
        </div>
        <div class="flex rounded-lg overflow-hidden border border-n-weak">
          <button
            v-for="s in [
              { key: 'all', label: 'Tüm mağazalar' },
              { key: 'koza', label: 'Koza' },
              { key: 'modax', label: 'ModaXLarge' },
            ]"
            :key="s.key"
            class="px-3 py-2 text-sm"
            :class="seller === s.key ? 'bg-violet-600 text-white' : 'bg-n-solid-1 text-n-slate-11 hover:bg-n-solid-2'"
            @click="seller = s.key"
          >
            {{ s.label }}
          </button>
        </div>
      </div>

      <div v-if="errorMsg" class="mb-4 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-4 py-3 text-sm">
        {{ errorMsg }}
      </div>

      <div v-if="loading" class="text-center py-16 text-n-slate-11">Yükleniyor…</div>

      <!-- BEKLEYENLER -->
      <template v-else-if="tab === 'bekleyen'">
        <div v-if="!filteredPending.length" class="rounded-xl border border-n-weak bg-n-solid-1 p-10 text-center">
          <div class="text-4xl mb-2">✅</div>
          <div class="text-lg font-semibold text-n-slate-12">Cevap bekleyen soru yok</div>
          <div class="text-sm text-n-slate-11 mt-1">Yeni soru geldiğinde burada görünecek (10 dakikada bir kontrol edilir).</div>
        </div>

        <div v-for="q in filteredPending" :key="q.id" class="rounded-xl border border-n-weak bg-n-solid-1 p-5 mb-4">
          <div class="flex gap-4">
            <img
              v-if="q.product_image_url"
              :src="q.product_image_url"
              class="w-16 h-20 rounded-lg object-cover flex-none"
              alt=""
            />
            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center gap-2 mb-1">
                <span class="px-2 py-0.5 rounded text-xs font-semibold" :class="q.seller === 'koza' ? 'bg-woot-100 text-woot-800 dark:bg-woot-800/40 dark:text-woot-200' : 'bg-ruby-100 text-ruby-800 dark:bg-ruby-800/40 dark:text-ruby-200'">
                  {{ SELLER_LABELS[q.seller] || q.seller }}
                </span>
                <span class="px-2 py-0.5 rounded text-xs font-semibold" :class="catMeta(q.category).cls">
                  {{ catMeta(q.category).label }}
                </span>
                <span class="text-xs text-n-slate-11">{{ fmtTime(q.asked_at) }}</span>
                <span class="text-xs" :class="waitCls(q.bekleme_dk)">⏱️ {{ fmtWait(q.bekleme_dk) }} bekliyor</span>
              </div>
              <a
                v-if="q.product_web_url"
                :href="q.product_web_url"
                target="_blank"
                rel="noopener"
                class="text-sm text-n-slate-11 hover:text-woot-600 truncate block"
              >
                {{ q.product_name }}
              </a>
              <p class="text-base font-medium text-n-slate-12 mt-2 whitespace-pre-wrap">“{{ q.text }}”</p>
            </div>
          </div>

          <div class="mt-4">
            <div v-if="q.category === 'siparis_sikayet'" class="rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-800 dark:text-red-200 mb-2">
              Bu soru sipariş/şikayet içeriyor. Yapay zekâ bu tip sorulara taslak yazmaz — cevabı aşağıya kendiniz yazın.
            </div>
            <label class="text-xs font-semibold text-n-slate-11 uppercase">Cevap taslağı (düzenleyebilirsiniz)</label>
            <textarea
              v-model="q.draft_text"
              rows="3"
              class="mt-1 w-full rounded-lg border border-n-weak bg-n-background text-n-slate-12 p-3 text-base"
              placeholder="Cevabınızı yazın… (en az 10 karakter)"
              @blur="saveDraft(q)"
            />
            <div class="flex flex-wrap items-center justify-between gap-2 mt-2">
              <span class="text-xs text-n-slate-11">
                {{ savingId === q.id ? 'Taslak kaydediliyor…' : 'Gönderilen cevap Trendyol onayından geçtikten sonra müşteriye görünür.' }}
              </span>
              <button
                class="px-5 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-60"
                :disabled="sendingId === q.id || (q.draft_text || '').trim().length < 10"
                @click="sendAnswer(q)"
              >
                {{ sendingId === q.id ? 'Gönderiliyor…' : '✓ Onayla ve Gönder' }}
              </button>
            </div>
          </div>
        </div>
      </template>

      <!-- CEVAPLANANLAR -->
      <template v-else>
        <div v-if="!filteredClosed.length" class="rounded-xl border border-n-weak bg-n-solid-1 p-10 text-center text-n-slate-11">
          Henüz cevaplanmış soru yok.
        </div>
        <div v-for="q in filteredClosed" :key="q.id" class="rounded-xl border border-n-weak bg-n-solid-1 p-4 mb-3">
          <div class="flex flex-wrap items-center gap-2 mb-1">
            <span class="px-2 py-0.5 rounded text-xs font-semibold" :class="q.seller === 'koza' ? 'bg-woot-100 text-woot-800 dark:bg-woot-800/40 dark:text-woot-200' : 'bg-ruby-100 text-ruby-800 dark:bg-ruby-800/40 dark:text-ruby-200'">
              {{ SELLER_LABELS[q.seller] || q.seller }}
            </span>
            <span class="px-2 py-0.5 rounded text-xs" :class="catMeta(q.category).cls">{{ catMeta(q.category).label }}</span>
            <span v-if="q.status === 'rejected'" class="px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-800/40 dark:text-red-200">
              Trendyol moderasyonu REDDETTİ
            </span>
            <span class="text-xs text-n-slate-11">{{ fmtTime(q.asked_at) }} → {{ fmtTime(q.answered_at) }}</span>
            <span class="text-xs text-n-slate-11">{{ SOURCE_LABELS[q.answer_source] || '' }}</span>
          </div>
          <div class="text-sm text-n-slate-11 truncate">{{ q.product_name }}</div>
          <p class="text-sm text-n-slate-12 mt-1">S: {{ q.text }}</p>
          <p class="text-sm text-green-800 dark:text-green-300 mt-1">C: {{ q.answer_text }}</p>
        </div>
      </template>
    </div>
  </div>
</template>
